import React, { useContext, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useLoader } from '../../api/useLoader';
import { NavLink } from 'react-router-dom';
import { PraksisstedApiContext } from '../../api/PraksisstedApiContext';
import {
  BrukerinfoDto,
  BrukerrollerDto,
  PersonDto,
  StudentfordelingDto,
  StudentfordelingMedValgDto,
} from '../../api/generated/praksissted-generated-api';
import { useIntl } from 'react-intl';
import translations from './fordeling.i18n';
import Spinner from '../../components/spinner/spinner';
import * as css from './fordeling.module.scss';
import Button from '../../components/button';
import { HttpError } from '../../api/generated/studiested-generated-api';
import { PsaUserContext } from '../../user/PsaUserStore';
import { Alert } from '../../components/alert';
import { EmptyState } from '../../components/emptyState/emptyState';
import Dropdown from '../../components/input/dropdown';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
  RowBackground,
} from '../../components/table/alpTable';
import useUnsavedChangesPrompt from '../../hooks/useUnsavedChangesPrompt';
import {
  OnStedHoverCard,
  StedType,
} from '../../components/hierarchy/onhoverhierarchyinfo';
import { useActivityContextPSA } from '../activitycontexts';
import FordelingDownloadButtons from './FordelingDownloadButtons';
import { compareStudents, getFullName, navnSearch } from './fordeling.uha';
import { useFormatMessageFor } from '../../i18n/translation';

const Fordeling = () => {
  const { praksiskalenderApi } = useContext(PraksisstedApiContext);
  const { praksisaktivitet } = useActivityContextPSA();
  const { id: aktivitetsId } = praksisaktivitet;

  const { data, loading, error, reload } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdFordelingGet({
        pathParams: { id: aktivitetsId },
      }),
    [aktivitetsId],
    false,
  );

  const labels = useFormatMessageFor(translations);

  return (
    <>
      {loading && <Spinner />}
      {data && (
        <FordelingWithDataUserWrapper
          studentfordelingMedValgDto={data}
          reload={reload}
        />
      )}
      {error && (
        <EmptyState
          title={labels.noAssignmentsTitle}
          text={labels.noAssignmentsText}
        />
      )}
    </>
  );
};

interface FordelingWithDataProps {
  studentfordelingMedValgDto: StudentfordelingMedValgDto;
  reload: () => void;
  psaUser: BrukerinfoDto;
}

const FordelingWithDataUserWrapper: React.FC<
  React.PropsWithChildren<Omit<FordelingWithDataProps, 'psaUser'>>
> = ({ ...props }) => {
  const { psaUser } = useContext(PsaUserContext);

  return psaUser ? <FordelingWithData psaUser={psaUser} {...props} /> : <></>;
};

type UserRightsType = {
  isAdmin: boolean;
  canWrite: boolean;
};

type BrukerrolleToUserRights = { [key in BrukerrollerDto]: UserRightsType };

const UserRights: BrukerrolleToUserRights = {
  [BrukerrollerDto.Administrator]: { isAdmin: true, canWrite: true },
  [BrukerrollerDto.Skriverettighet]: { isAdmin: false, canWrite: true },
  [BrukerrollerDto.Leserettighet]: { isAdmin: false, canWrite: false },
};

const getUserRights = (hoeyesteRettighet: BrukerrollerDto): UserRightsType => {
  switch (hoeyesteRettighet) {
    case BrukerrollerDto.Administrator:
      return UserRights[BrukerrollerDto.Administrator];
    case BrukerrollerDto.Skriverettighet:
      return UserRights[BrukerrollerDto.Skriverettighet];
    case BrukerrollerDto.Leserettighet:
      return UserRights[BrukerrollerDto.Leserettighet];
  }
};

const FordelingWithData: React.FC<
  React.PropsWithChildren<FordelingWithDataProps>
> = ({ studentfordelingMedValgDto, reload, psaUser }) => {
  const { praksisaktivitet } = useActivityContextPSA();
  const { hoeyesteRettighet } = psaUser;
  const { isAdmin, canWrite } = useMemo(
    () => getUserRights(hoeyesteRettighet),
    [hoeyesteRettighet],
  );
  const { locale } = useIntl();
  const { praksiskalenderApi } = useContext(PraksisstedApiContext);
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );
  const { studentfordeling } = studentfordelingMedValgDto;

  const [studentsWithoutVeiledere, setStudentsWithoutVeiledere] = useState<
    string[]
  >(
    studentfordeling
      .filter((fordeling) => fordeling.veiledere.length === 0)
      .map((fordeling) => fordeling.student.id),
  );
  const [activeCol, setActiveCol] = useState<number>(-1);
  const [search, setSearch] = useState<string[]>(['', '', '', '']);
  const [sortedFordelinger, setSortedFordelinger] =
    useState<StudentfordelingDto[]>();

  const labels = useFormatMessageFor({
    ...translations,
    missingVeileder: {
      ...translations.missingVeileder,
      values: { numStudents: studentsWithoutVeiledere.length.toString() },
    },
  });

  const existingVeilederAssignment = React.useMemo(
    () =>
      studentfordeling.reduce(
        (
          newObj: { [studentId: string]: string[] },
          fordeling: StudentfordelingDto,
        ) => {
          newObj[fordeling.student.id] = fordeling.veiledere.map(
            (veileder) => veileder.id,
          );
          return newObj;
        },
        {},
      ),
    [studentfordeling],
  );

  const [currentVeiledere, setCurrentVeiledere] = useState(
    existingVeilederAssignment,
  );

  const changeVeiledereForStudent = (
    studentId: string,
    veiledere: string[],
  ) => {
    setCurrentVeiledere({
      ...currentVeiledere,
      [studentId]: veiledere,
    });

    const listWithoutStudent = studentsWithoutVeiledere.filter(
      (id) => id !== studentId,
    );
    if (veiledere.length === 0) {
      setStudentsWithoutVeiledere([...listWithoutStudent, studentId]);
    } else {
      setStudentsWithoutVeiledere(listWithoutStudent);
    }
  };

  const containsChange: boolean = React.useMemo(
    () =>
      Object.entries(currentVeiledere).some(([studentId, veiledere]) => {
        const existingVeiledere = existingVeilederAssignment[studentId] || [];
        return (
          existingVeiledere.length !== veiledere.length ||
          veiledere.some((id) => !existingVeiledere.includes(id))
        );
      }),
    [existingVeilederAssignment, currentVeiledere],
  );

  const headers = [
    { title: labels.student },
    { title: labels.underviser },
    { title: labels.praksissted },
    { title: labels.veileder },
  ];

  const missingVeilederText =
    studentsWithoutVeiledere.length === 1
      ? labels.missingSingleVeileder
      : labels.missingVeileder;

  const saveVeiledere = () => {
    praksiskalenderApi
      .praksisaktivitetIdFordelingPut({
        pathParams: { id: praksisaktivitet.id },
        studentfordelingPutDto: Object.entries(currentVeiledere).map(
          ([studentId, veilederIder]) => {
            return { studentId, veilederIder };
          },
        ),
      })
      .then(() => {
        addSuccessNotification(labels.lagret);
        reload();
      })
      .catch((err: HttpError) => {
        addErrorNotification(err.body);
      });
  };

  const filteredAndSortedFordelinger = React.useMemo(() => {
    return (sortedFordelinger || studentfordeling).filter((fordeling) => {
      const { fornavn, etternavn, id } = fordeling.student;
      const filterStudent =
        search[0].length < 2 || navnSearch(fornavn, etternavn, search[0]);
      const filterUnderviser =
        search[1].length < 2 ||
        fordeling.undervisere.some((underviser) =>
          navnSearch(underviser.fornavn, underviser.etternavn, search[1]),
        );
      const filterPlassering =
        search[2].length < 2 ||
        fordeling.praksissted.navn.toLowerCase().indexOf(search[2]) > -1;
      const filterVeileder =
        search[3].length < 2 ||
        Object.entries(currentVeiledere).some(([studentId, veiledere]) => {
          if (studentId !== id) return false;

          return veiledere.some((veilederId) => {
            const veileder = fordeling.muligeVeiledere.find(
              (v) => v.id === veilederId,
            );

            return (
              veileder &&
              navnSearch(veileder.fornavn, veileder.etternavn, search[3])
            );
          });
        });

      return (
        filterStudent && filterUnderviser && filterPlassering && filterVeileder
      );
    });
  }, [search, sortedFordelinger, studentfordeling, currentVeiledere]);

  const getFordelingRows = () => {
    return filteredAndSortedFordelinger.map((fordeling) => {
      const studentId = fordeling.student.id;
      const existingVeiledereForStudent = existingVeilederAssignment[studentId];
      const currentVeilederIdsForStudent = currentVeiledere[studentId];

      const hasChangedVeiledere =
        existingVeiledereForStudent.length !==
          currentVeilederIdsForStudent.length ||
        existingVeiledereForStudent.some(
          (id) => !currentVeilederIdsForStudent.includes(id),
        );

      return (
        <FordelingTableRow
          key={fordeling.student.id}
          fordeling={fordeling}
          muligeVeiledere={fordeling.muligeVeiledere}
          changeVeiledereForStudent={changeVeiledereForStudent}
          hasChangedVeiledere={hasChangedVeiledere}
          headers={headers}
          veilederIds={currentVeilederIdsForStudent}
          canWrite={canWrite}
        />
      );
    });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: StudentfordelingDto,
    b: StudentfordelingDto,
  ) => {
    switch (col) {
      case 0:
        return compareStudents(a.student, b.student, compare);
      case 2:
        return compare(a.praksissted.navn, b.praksissted.navn);
    }
    return 0;
  };

  const sortStudentsByActiveCol = (col: number, ascending: boolean) => {
    setSortedFordelinger(
      [...studentfordeling].sort((a, b) =>
        ascending ? sort(col, b, a) : sort(col, a, b),
      ),
    );
  };

  const canSortIndices = [0, 2];
  const canSearchIndices = [0, 1, 2, 3];

  const getHeaders = () =>
    headers.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortStudentsByActiveCol}
        canSort={canSortIndices.includes(index)}
        search={(col, value) =>
          setSearch(
            search.map((searchValue, i) =>
              i === col ? value.toLowerCase() : searchValue,
            ),
          )
        }
        canSearch={canSearchIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  useUnsavedChangesPrompt(containsChange);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Table>
            <TableHeader>
              <TableRow top>{getHeaders()}</TableRow>
            </TableHeader>
            <TableMain>{getFordelingRows()}</TableMain>
          </Table>
        </Grid>
        <div className={css.buttonContainer}>
          <FordelingDownloadButtons
            isAdmin={isAdmin}
            brukerId={psaUser.id}
            aktivitetId={praksisaktivitet.id}
            delIdentitetsnummer={!!praksisaktivitet.delIdentitetsnummer}
            isStudiestedbruker={false}
          />
          {canWrite && (
            <Button.Secondary
              id={'bekreftVeiledere'}
              disabled={!containsChange}
              onClick={saveVeiledere}
            >
              {labels.bekreftVeiledere}
            </Button.Secondary>
          )}
        </div>
      </Grid>
      {studentsWithoutVeiledere.length > 0 && (
        <Alert>{missingVeilederText}</Alert>
      )}
    </>
  );
};

interface FordelingTableRowProps {
  fordeling: StudentfordelingDto;
  muligeVeiledere: PersonDto[];
  changeVeiledereForStudent: (studentId: string, veiledere: string[]) => void;
  hasChangedVeiledere: boolean;
  headers: { title: string }[];
  veilederIds: string[];
  canWrite: boolean;
}

const FordelingTableRow: React.FC<
  React.PropsWithChildren<FordelingTableRowProps>
> = ({
  fordeling,
  muligeVeiledere,
  changeVeiledereForStudent,
  hasChangedVeiledere,
  headers,
  veilederIds,
  canWrite,
}) => {
  const isStudentActive = fordeling.student.fsId != null;

  const labels = useFormatMessageFor(translations);

  const { student, praksissted, undervisere, avtalefeil } = fordeling;

  const onVeilederChanged = (veilederIds: string[]) => {
    changeVeiledereForStudent(student.id, veilederIds);
  };

  const veilederNames = useMemo(
    () =>
      muligeVeiledere
        .filter((veileder) => veilederIds.includes(veileder.id))
        .map((veileder) => ({
          id: veileder.id,
          navn: getFullName(veileder),
          checked: true,
        })),
    [muligeVeiledere, veilederIds],
  );

  const studentnavn = getFullName(fordeling.student);
  const hideBackgroundAndBorder =
    !hasChangedVeiledere && veilederIds.length > 0;

  let tabHeader = 0;
  return (
    <TableRow
      rowBackground={avtalefeil ? RowBackground.alert : RowBackground.default}
      title={avtalefeil ? labels.avtalefeilPS : undefined}
      data-cy={student.id}
    >
      <TableCellTd header={headers[tabHeader++].title}>
        {isStudentActive ? (
          <NavLink to={`/students/studentbrukerprofil/${student.fsId}`}>
            {studentnavn}
          </NavLink>
        ) : (
          studentnavn
        )}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {undervisere &&
          undervisere.map((underviser, index) => (
            <React.Fragment key={`${underviser.id}-${index}`}>
              <NavLink to={`/studiestedbrukerprofil/${underviser.id}`}>
                {getFullName(underviser)}
              </NavLink>
              {index < undervisere.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        <div className={css.flex}>
          {praksissted.navn}
          <OnStedHoverCard
            id={praksissted.id}
            navn={praksissted.navn}
            sted={StedType.PRAKSISSTED}
          />
        </div>
      </TableCellTd>
      <TableCellTd header={headers[tabHeader].title}>
        <div className={css.selectMaxWidth}>
          {muligeVeiledere.length > 0 ? (
            <Dropdown
              id={`select-veileder-${fordeling.student.id}`}
              ariaLabel={'supervisor-select-dropdown'}
              onChange={(event, value) => {
                onVeilederChanged(
                  value.map((veileder: PersonDto) => veileder.id),
                );
              }}
              selectedOptions={veilederNames}
              options={[
                ...muligeVeiledere.map((veileder) => ({
                  id: veileder.id,
                  navn: getFullName(veileder),
                  checked: veilederIds.includes(veileder.id),
                  deaktivert: !canWrite,
                })),
              ]}
              placeholder={labels.velgVeileder}
              hideBackground={hideBackgroundAndBorder}
              hideBorder={hideBackgroundAndBorder}
              separator={';'}
              highlightPlaceholder
              multiple
            />
          ) : (
            labels.noVeilederePSA
          )}
        </div>
      </TableCellTd>
    </TableRow>
  );
};

export default Fordeling;
